import { useState } from 'react';
import moment from 'moment';
import { useQuery } from 'react-query';
import rota from '../../network/rota';
import { Container } from '../../ui/Container';
import { Header, HeaderTitle, HeaderAction } from '../../ui/Header';
import { Button } from '../../ui/Button';

const WEEKS_SHOWN = 4;

/**
 * Read-only list of the signed-in employee's published shifts, four weeks
 * at a time.
 */
export function MyShiftsRoute() {
  const [weekStart, setWeekStart] = useState(() =>
    moment().startOf('isoWeek'),
  );
  const from = weekStart.format('YYYY-MM-DD');
  const to = weekStart
    .clone()
    .add(WEEKS_SHOWN, 'weeks')
    .subtract(1, 'day')
    .format('YYYY-MM-DD');

  const { data: shifts = [], isLoading, isError, refetch } = useQuery(
    ['my-shifts', from, to],
    () => rota.getMySchedules(from, to),
    { retry: false },
  );

  return (
    <Container>
      <Header className="mb-8">
        <HeaderTitle>My shifts</HeaderTitle>
        <HeaderAction>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              aria-label="Previous weeks"
              onClick={() =>
                setWeekStart((w) => w.clone().subtract(WEEKS_SHOWN, 'weeks'))
              }
            >
              Previous
            </Button>
            <Button
              variant="outline"
              onClick={() => setWeekStart(moment().startOf('isoWeek'))}
            >
              This week
            </Button>
            <Button
              variant="outline"
              aria-label="Next weeks"
              onClick={() =>
                setWeekStart((w) => w.clone().add(WEEKS_SHOWN, 'weeks'))
              }
            >
              Next
            </Button>
          </div>
        </HeaderAction>
      </Header>

      <p className="mb-4 text-sm text-gray-600">
        {moment(from).format('D MMM YYYY')} to {moment(to).format('D MMM YYYY')}
      </p>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading your shifts...</p>
      ) : isError ? (
        <div role="alert" className="flex items-center gap-3 text-sm text-red-700">
          Your shifts could not be loaded.
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : shifts.length === 0 ? (
        <p className="text-sm text-gray-500">
          You have no published shifts in this period.
        </p>
      ) : (
        <ul className="divide-y divide-gray-100 rounded-lg border border-gray-200 bg-white">
          {shifts.map((shift) => {
            const start = moment(shift.start_date);
            const end = moment(shift.end_date);
            const sameDay = start.isSame(end, 'day');
            return (
              <li key={shift.id} className="flex items-center gap-4 px-4 py-3 text-sm">
                <span
                  aria-hidden="true"
                  className="h-8 w-1 rounded"
                  style={{ backgroundColor: shift.color || '#94a3b8' }}
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{shift.name}</p>
                  <p className="text-gray-600">
                    {sameDay
                      ? start.format('ddd D MMM')
                      : `${start.format('ddd D MMM')} to ${end.format('ddd D MMM')}`}
                  </p>
                </div>
                <p className="whitespace-nowrap font-medium text-gray-700">
                  {shift.start_time} to {shift.end_time}
                </p>
              </li>
            );
          })}
        </ul>
      )}
    </Container>
  );
}
